import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { newIdempotencyKey } from '#/api/crmMgmt/customerOrder/collection';
import { getCustomerOrderApiErrorMessage } from '#/modules/crmMgmt/customerOrder/helper/apiError';
import { customerOrderDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import { collectionQueryKeyPrefix } from './collectionQueryKeys';

/**
 * 收款寫入共用：
 * - 一個對話框（掛載期間）＝一把 Idempotency-Key，失敗重送沿用；成功後換新。
 * - 成功或 409 都重抓收款資料與客訂 detail（子單可能 PAID↔INVOICED）。
 */
export default function useCollectionMutation(customerOrderId, mutationFn, {
  successMessage = '操作成功',
  onSuccess,
} = {}) {
  const queryClient = useQueryClient();
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  async function refreshAll() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: collectionQueryKeyPrefix(customerOrderId) }),
      queryClient.invalidateQueries({ queryKey: customerOrderDetailQueryKey(customerOrderId) }),
    ]);
  }

  return useMutation({
    retry: false,
    mutationFn: (variables) => mutationFn(variables, { idempotencyKey }),
    onSuccess: async (result, variables) => {
      toast.success(result?.message || successMessage);
      setIdempotencyKey(newIdempotencyKey());
      await refreshAll();
      onSuccess?.(result, variables);
    },
    onError: async (error) => {
      toast.error(getCustomerOrderApiErrorMessage(error));
      if (error?.response?.status === 409) {
        await refreshAll();
      }
    },
  });
}
